import { skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { AxiosBaseQueryFunction } from '../../axiosBaseQuery';
import { useSelectedStore } from '../stores-api/useSelectedStore';
import {
  CfiAddress,
  CfiCompany,
  CfiCompanyQueryParams,
  normalizeCfiAddress,
  useFetchCfisQuery,
} from './cfisService';

import { shouldFetchFn } from '~/api/utils';
import useFetchedEstimate from '~/core/hooks/useFetchedEstimate';
import { useSelectedColor } from '~/core/hooks/useSelectedColor';
import { useAppSelector } from '~/redux/hooks';
import { selectZipcode } from '~/redux/slices/zipcode';

type useCfisReturn = TypedUseQueryHookResult<
  CfiCompany[],
  CfiCompanyQueryParams,
  AxiosBaseQueryFunction
> & {
  cfi?: CfiCompany;
};

export const useCfiQueryArgs = (
  addressOverride?: CfiAddress
): CfiCompanyQueryParams | typeof skipToken => {
  const zipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const { selectedValue: selectedColor } = useSelectedColor();
  const { fetchedEstimate } = useFetchedEstimate();
  const effectiveZipCode = fetchedEstimate?.customerZip ?? zipCode;
  const installAddress = normalizeCfiAddress(
    addressOverride ?? {
      address: fetchedEstimate?.customerAddress1,
      city: fetchedEstimate?.customerCity,
      state: fetchedEstimate?.customerState,
    }
  );
  const shouldFetch = shouldFetchFn({
    args: [effectiveZipCode, selectedStore?.companyId, selectedColor?.qteGrpID],
  });

  if (
    !shouldFetch ||
    !effectiveZipCode ||
    !selectedStore?.companyId ||
    !selectedColor?.qteGrpID
  ) {
    return skipToken;
  }

  return {
    params: {
      zipCode: effectiveZipCode,
      storeId: selectedStore.companyId,
      prodQteGrpID: selectedColor.qteGrpID,
      ...installAddress,
    },
  };
};

export const useCfis = (addressOverride?: CfiAddress): useCfisReturn => {
  const queryArgs = useCfiQueryArgs(addressOverride);
  const { cfi, ...rest } = useFetchCfisQuery(queryArgs, {
    selectFromResult: (result) => ({
      ...result,
      cfi: result.isError ? undefined : result.currentData?.[0],
    }),
  });

  return { cfi, ...rest };
};
